import { z } from '../zod.js';
/**
 * The calendar definition (1.0b deliverable 3): P0-13's draft completed for baseline section 6
 * and amendments 2.2 sections 2 and 7, reconciled with 1.K-1. It is what `calendar.definition`
 * and `calendar_version.definition` store, what the Foundry import fills and the export reads,
 * and what the calendar editor edits; the engine compiles it into an in-memory form.
 *
 * **Per-field rules here, cross-field rules in `compile`.** This schema checks types,
 * nullability, enums, strict objects and finite numbers, and every per-field rule the engine
 * relies on: integers wherever it counts; positive `leap.interval`, time units, `cycleLength` and
 * `approx` values; non-negative `days` and `leapDays`; an era date's `day` from 1; unique keys
 * within each list; at least one month, one weekday and one era; `firstWeekday` from 0 and below
 * the weekday count; and each season's `startDay` from 0 to the common year's days minus one.
 * 1.K-1's `compile` reports each issue of this schema as `invalid_shape` at its path and adds the
 * rules no field can state (`no_regular_month`, `duplicate_ordinal`, `bad_month_length`,
 * `bad_season_start`, `bad_era`, `calendar_too_large`). One rule has one home and one code, so
 * each breach of a rule below raises exactly one issue.
 *
 * **No `intercalaryDays`.** Intercalary days are months whose `intercalary` is true, Foundry's own
 * model: a festival day is a one-day intercalary month; a leap-only day is an intercalary month
 * with `days: 0` and `leapDays: 1`. Every object is strict, so an `intercalaryDays` key is refused.
 */
const Int = z.number().int();
const NonNegativeInt = z.number().int().min(0);
const PositiveInt = z.number().int().min(1);
/** A day of an absolute year: the year, the key of any month (regular or intercalary), a day from 1. */
export const CalendarDate = z.strictObject({
    year: Int,
    month: z.string(),
    day: PositiveInt,
});
/** A month, regular or intercalary, ordered by `ordinal` as Foundry orders it. */
export const CalendarMonth = z.strictObject({
    key: z.string(),
    name: z.string(),
    abbreviation: z.string().nullable(),
    ordinal: Int,
    /** Its length in a common year; 0 for a leap-only month. */
    days: NonNegativeInt,
    startingWeekday: Int.nullable(),
    /** Its length in a leap year, when that differs. */
    leapDays: NonNegativeInt.nullable(),
    intercalary: z.boolean().nullable(),
    /** Kept for the Foundry export; the engine ignores it. */
    dayOffset: z.number().nullable(),
});
export const CalendarWeekday = z.strictObject({
    key: z.string(),
    name: z.string(),
    abbreviation: z.string().nullable(),
    ordinal: Int,
    isRestDay: z.boolean().nullable(),
});
export const CalendarSeason = z.strictObject({
    key: z.string(),
    name: z.string(),
    abbreviation: z.string().nullable(),
    /** The 0-based day of the year the season starts on (`seasonDayBase` already removed). */
    startDay: NonNegativeInt,
    /** Foundry's own month bounds, kept for the export. */
    foundry: z.strictObject({
        monthStart: z.number().nullable(),
        monthEnd: z.number().nullable(),
    }),
});
/** A moon, app-only (never exported to Foundry). */
export const CalendarMoon = z.strictObject({
    key: z.string(),
    name: z.string(),
    cycleLength: z.number().positive(),
    offset: z.number(),
});
/**
 * An era. `name` and `suffix` may each be empty (the imported era has both empty; `compile`
 * refuses another era with both empty, as `bad_era`). `start` null: the era reaches back without
 * limit. `end` is exclusive (the era ends where that day begins); null: it runs to the next era's
 * start, or without limit for the last. `anchor` pairs one absolute year with its era-year number.
 */
export const CalendarEra = z.strictObject({
    key: z.string(),
    name: z.string(),
    suffix: z.string(),
    direction: z.enum(['up', 'down']),
    start: CalendarDate.nullable(),
    end: CalendarDate.nullable(),
    anchor: z.strictObject({ year: Int, eraYear: Int }),
});
/** A list whose items carry `key`: each later item that repeats a key is an issue at its `key`. */
function withUniqueKeys(item) {
    return z.array(item).superRefine((items, ctx) => {
        const seen = new Set();
        items.forEach((entry, index) => {
            const key = entry?.key;
            if (typeof key !== 'string')
                return;
            if (seen.has(key)) {
                ctx.addIssue({ code: 'custom', message: 'duplicate key', path: [index, 'key'] });
            }
            seen.add(key);
        });
    });
}
/** Days of a common year: the sum of every month's `days`, or null when one is not valid. */
function commonYearDays(months) {
    if (!Array.isArray(months) || months.length === 0)
        return null;
    let total = 0;
    for (const month of months) {
        const days = month?.days;
        if (typeof days !== 'number' || !Number.isSafeInteger(days) || days < 0)
            return null;
        total += days;
    }
    return total;
}
export const CalendarDefinition = z
    .strictObject({
    name: z.string(),
    description: z.string(),
    /** The absolute year that begins at tick 0. */
    yearZero: Int,
    /** The 0-based weekday index of the first day of `yearZero`. */
    firstWeekday: NonNegativeInt,
    /** Leap when `year ≥ start` and `(year − start) mod interval = 0`; null without a leap rule. */
    leap: z.strictObject({ start: Int, interval: PositiveInt }).nullable(),
    months: withUniqueKeys(CalendarMonth).min(1),
    weekdays: withUniqueKeys(CalendarWeekday).min(1),
    time: z.strictObject({
        hoursPerDay: PositiveInt,
        minutesPerHour: PositiveInt,
        secondsPerMinute: PositiveInt,
    }),
    seasons: withUniqueKeys(CalendarSeason),
    moons: withUniqueKeys(CalendarMoon),
    eras: withUniqueKeys(CalendarEra).min(1),
    /** The half-widths of "c." per precision: years, decades, months, days. */
    approx: z.strictObject({
        year: PositiveInt,
        decade: PositiveInt,
        month: PositiveInt,
        day: PositiveInt,
    }),
    foundry: z.strictObject({
        /** `tick = worldTime + worldTimeOffset`. */
        worldTimeOffset: Int,
        /** Which day the imported file's season bounds counted from. */
        seasonDayBase: z.union([z.literal(0), z.literal(1)]),
    }),
    source: z.strictObject({
        format: z.enum(['foundry-calendar-config', 'app']),
        /** Keys of the imported file the app does not model, by JSON pointer; never exported. */
        extras: z.record(z.string(), z.unknown()),
    }),
})
    .superRefine((definition, ctx) => {
    // Each rule reads only values whose own per-field rules hold, so a breach elsewhere is
    // reported once, where it is, and never again here.
    const weekdayCount = Array.isArray(definition.weekdays) ? definition.weekdays.length : 0;
    const first = definition.firstWeekday;
    if (weekdayCount > 0 && Number.isSafeInteger(first) && first >= weekdayCount) {
        ctx.addIssue({
            code: 'custom',
            message: 'firstWeekday must be below the number of weekdays',
            path: ['firstWeekday'],
        });
    }
    const yearDays = commonYearDays(definition.months);
    if (yearDays !== null && Array.isArray(definition.seasons)) {
        definition.seasons.forEach((season, index) => {
            const start = season?.startDay;
            if (typeof start === 'number' && Number.isSafeInteger(start) && start >= yearDays) {
                ctx.addIssue({
                    code: 'custom',
                    message: "a season must start within the common year's days",
                    path: ['seasons', index, 'startDay'],
                });
            }
        });
    }
});
//# sourceMappingURL=definition.js.map